import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
// Styles
import "./VColorPickerSwatches.css";

// Components
import { VIcon } from "../VIcon/index.js"; // Composables
import { useColumnFocus } from "./columnFocus.js";
import { makeComponentProps } from "../../composables/component.js";
import { useRtl } from "../../composables/locale.js"; // Utilities
import { toRef } from 'vue';
import { convertToUnit, deepEqual, defineComponent, getContrast, parseColor, propsFactory, RGBtoCSS, RGBtoHSV, useRender } from "../../util/index.js";
import colors from "../../util/colors.js"; // Types
export const makeVColorPickerSwatchesProps = propsFactory({
  swatches: {
    type: Array,
    default: () => parseDefaultColors(colors)
  },
  disabled: Boolean,
  readonly: Boolean,
  color: Object,
  maxHeight: [Number, String],
  ...makeComponentProps()
}, 'VColorPickerSwatches');
function parseDefaultColors(colors) {
  return Object.keys(colors).map(key => {
    const color = colors[key];
    return color.base ? [color.base, color.darken4, color.darken3, color.darken2, color.darken1, color.lighten1, color.lighten2, color.lighten3, color.lighten4, color.lighten5] : [color.black, color.white, color.transparent];
  });
}
export const VColorPickerSwatches = defineComponent({
  name: 'VColorPickerSwatches',
  props: makeVColorPickerSwatchesProps(),
  emits: {
    'update:color': color => true
  },
  setup(props, {
    emit
  }) {
    const isInteractive = toRef(() => !props.disabled && !props.readonly);
    const {
      isRtl
    } = useRtl();
    const columnFocus = useColumnFocus(() => props.swatches.map(swatch => swatch.length), {
      disabled: () => props.disabled,
      rtl: isRtl
    });
    function onSwatchClick(hsva) {
      if (!isInteractive.value || !hsva) {
        return;
      }
      emit('update:color', hsva);
    }
    function onSwatchKeydown(event, hsva) {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSwatchClick(hsva);
        return;
      }
      if (event.key === ' ') {
        event.preventDefault();
      }
    }
    function onSwatchKeyup(event, hsva) {
      if (event.key === ' ') {
        onSwatchClick(hsva);
      }
    }
    useRender(() => _createElementVNode("div", {
      "class": _normalizeClass(['v-color-picker-swatches', props.class]),
      "style": _normalizeStyle([{
        maxHeight: convertToUnit(props.maxHeight)
      }, props.style])
    }, [_createElementVNode("div", {
      "ref": columnFocus.containerEl,
      "onKeydown": columnFocus.onKeydown
    }, [props.swatches.map((swatch, column) => _createElementVNode("div", {
      "class": "v-color-picker-swatches__swatch"
    }, [swatch.map((color, row) => {
      const rgba = parseColor(color);
      const hsva = RGBtoHSV(rgba);
      const background = RGBtoCSS(rgba);
      const isSelected = !!props.color && deepEqual(props.color, hsva);
      return _createElementVNode("div", {
        "ref": columnFocus.register(column, row),
        "class": _normalizeClass(['v-color-picker-swatches__color', {
          'v-color-picker-swatches__color--disabled': props.disabled
        }]),
        "role": "button",
        "tabindex": columnFocus.isTabbable(column, row) && !props.disabled ? 0 : -1,
        "aria-label": background,
        "aria-pressed": isSelected,
        "aria-disabled": props.disabled || props.readonly || undefined,
        "onFocus": () => columnFocus.onFocus(column, row),
        "onKeydown": event => onSwatchKeydown(event, hsva),
        "onKeyup": event => onSwatchKeyup(event, hsva),
        "onClick": () => onSwatchClick(hsva)
      }, [_createElementVNode("div", {
        "style": {
          background
        }
      }, [isSelected ? _createVNode(VIcon, {
        "size": "x-small",
        "icon": "$success",
        "color": getContrast(color, '#FFFFFF') > 2 ? 'white' : 'black'
      }, null) : undefined])]);
    })]))])]));
    return {};
  }
});
//# sourceMappingURL=VColorPickerSwatches.js.map